import type {
  BranchOrderSuggestion,
  FreightQuery,
  FreightReport,
  OnTheWayQuery,
  OnTheWayReport,
  SupplierPerformanceQuery,
  SupplierPerformanceReport,
  Adjustment,
  AdjustmentCreateBody,
  AdjustmentDecideInput,
  AdjustmentListQuery,
  AdjustmentSummary,
  CountPreview,
  BranchOrder,
  BranchOrderCreateBody,
  BranchOrderDeclineInput,
  BranchOrderListQuery,
  BranchOrderSendBody,
  BranchOrderSummary,
  BranchOrderUpdateBody,
  NotificationSettings,
  NotificationSettingInput,
  ExchangeRate,
  ActiveToggleInput,
  AppConfig,
  AttachmentCreateInput,
  AuditEntry,
  AuditQuery,
  ChinaOrder,
  ChinaOrderCreateInput,
  ChinaOrderLine,
  ChinaOrderLineCreateInput,
  ChinaOrderLineUpdateInput,
  ChinaOrderListQuery,
  ChinaOrderPage,
  ChinaOrderSummary,
  ChinaOrderUpdateInput,
  CycleInfo,
  Dashboard,
  DisplayStockInput,
  LowStockPage,
  LowStockQuery,
  MovementPage,
  MovementReportQuery,
  MovementTotals,
  MovementTotalsQuery,
  SlowStockPage,
  SlowStockQuery,
  StockByLocationCell,
  StockImportDetail,
  StockImportRevertInput,
  StockImportSummary,
  StockByLocationPage,
  StockByLocationQuery,
  ValuationQuery,
  ValuationReport,
  InvoiceImportOptions,
  InvoicePreview,
  LineProgressInput,
  OrderLineListQuery,
  OrderLineRow,
  Shipment,
  ShipmentArriveInput,
  ShipmentCreateInput,
  ShipmentFromOrdersInput,
  ShipmentInvoiceCreateInput,
  ShipmentInvoiceUpdateInput,
  ShipmentLineCreateInput,
  ShipmentLineUpdateInput,
  ShipmentListQuery,
  InvoiceListQuery,
  InvoiceCheckInput,
  InvoiceTransferInput,
  InvoiceRegisterRow,
  Transfer,
  TransferSummary,
  TransferCreateInput,
  TransferReceiveInput,
  TransferListQuery,
  ShipmentNumberCartonsInput,
  ShipmentReceiveInput,
  ShipmentSummary,
  ShipmentUpdateInput,
  InFlightItem,
  Notification,
  NotificationListQuery,
  SerialsInput,
  StatusReasonInput,
  SupplierLineEditInput,
  UpdateMyProfileInput,
  ChinaOrderReceiveInput,
  VersionOnlyInput,
  Branch,
  BranchCreateInput,
  BranchUpdateInput,
  Category,
  CategoryCreateInput,
  CategoryUpdateInput,
  ChangePasswordInput,
  ImportReport,
  Item,
  ItemCreateInput,
  ItemPackingInput,
  ItemListQuery,
  ItemUpdateInput,
  ListQuery,
  LoginInput,
  LoginResponse,
  Me,
  Page,
  PermissionInfo,
  PresignedUpload,
  PresignUploadInput,
  ResetPasswordInput,
  Role,
  RoleCreateInput,
  RolePermissionsInput,
  RoleUpdateInput,
  Session,
  StockPage,
  StockQuery,
  Supplier,
  SupplierCreateInput,
  SupplierUpdateInput,
  User,
  UserCreateInput,
  UserUpdateInput,
  PartUpload,
  ImportJob,
  SheetImportStartInput,
} from '@igoal/shared';

/**
 * Typed client for the I-GOAL API, shared by the web app and the future
 * mobile app.
 *  - web:    createApiClient({ baseUrl: '', mode: 'cookie' })
 *  - mobile: createApiClient({ baseUrl: 'https://api…', mode: 'bearer', getToken })
 */
export interface ApiClientOptions {
  baseUrl: string;
  mode: 'cookie' | 'bearer';
  getToken?: () => string | null | Promise<string | null>;
  onUnauthenticated?: () => void;
  fetch?: typeof fetch;
}

export class ApiClientError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }

  /** Field errors from a VALIDATION_FAILED response, keyed by field */
  get fieldErrors(): Record<string, string> {
    if (this.code !== 'VALIDATION_FAILED' || !Array.isArray(this.details)) return {};
    return Object.fromEntries((this.details as { field: string; message: string }[]).map((d) => [d.field, d.message]));
  }
}

type Query = Record<string, string | number | boolean | undefined | null>;

const qs = (q?: Query) => {
  if (!q) return '';
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
};

export function newIdempotencyKey(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  return c?.randomUUID ? c.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function createApiClient(opts: ApiClientOptions) {
  const doFetch = opts.fetch ?? fetch.bind(globalThis);
  const root = `${opts.baseUrl.replace(/\/$/, '')}/api/v1`;

  async function headers(extra?: Record<string, string>) {
    const h: Record<string, string> = { Accept: 'application/json', ...extra };
    if (opts.mode === 'cookie') h['X-Igoal-Client'] = 'web';
    else {
      const token = await opts.getToken?.();
      if (token) h.Authorization = `Bearer ${token}`;
    }
    return h;
  }

  async function handle<T>(res: Response): Promise<T> {
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    const body = text ? JSON.parse(text) : undefined;
    if (!res.ok) {
      if (res.status === 401) opts.onUnauthenticated?.();
      throw new ApiClientError(res.status, body?.code ?? 'HTTP_ERROR', body?.message ?? res.statusText, body?.details);
    }
    return body as T;
  }

  async function request<T>(method: string, path: string, body?: unknown, extra?: { idempotent?: boolean }): Promise<T> {
    const h = await headers(body !== undefined ? { 'Content-Type': 'application/json' } : undefined);
    if (extra?.idempotent) h['Idempotency-Key'] = newIdempotencyKey();
    const res = await doFetch(`${root}${path}`, {
      method,
      headers: h,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      credentials: opts.mode === 'cookie' ? 'include' : 'omit',
    });
    return handle<T>(res);
  }

  async function upload<T>(path: string, file: Blob, fileName: string, fields?: Record<string, string>): Promise<T> {
    const form = new FormData();
    form.append('file', file, fileName);
    for (const [k, v] of Object.entries(fields ?? {})) form.append(k, v);
    const res = await doFetch(`${root}${path}`, {
      method: 'POST',
      headers: await headers(),
      body: form,
      credentials: opts.mode === 'cookie' ? 'include' : 'omit',
    });
    return handle<T>(res);
  }

  /** Download an Excel export as a Blob plus the server's file name */
  async function download(path: string, q?: Query): Promise<{ blob: Blob; fileName: string; pages?: number }> {
    const res = await doFetch(`${root}${path}${qs(q)}`, { headers: await headers(), credentials: opts.mode === 'cookie' ? 'include' : 'omit' });
    if (!res.ok) await handle(res);
    const cd = res.headers.get('Content-Disposition') ?? '';
    const fileName = /filename="([^"]+)"/.exec(cd)?.[1] ?? 'export.xlsx';
    const pages = res.headers.get('X-Share-Pages');
    return { blob: await res.blob(), fileName, ...(pages ? { pages: Number(pages) } : {}) };
  }

  /** A share picture (PNG, no prices): page 1 says how many pages there are */
  const sharePicture = (path: string) => (page = 1) => download(path, { page }) as Promise<{ blob: Blob; fileName: string; pages: number }>;

  const get = <T>(path: string, q?: Query) => request<T>('GET', `${path}${qs(q)}`);
  const post = <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}, { idempotent: true });
  const patch = <T>(path: string, body: unknown) => request<T>('PATCH', path, body, { idempotent: true });
  const put = <T>(path: string, body: unknown) => request<T>('PUT', path, body, { idempotent: true });
  const del = <T>(path: string) => request<T>('DELETE', path);

  return {
    request,
    download,

    system: {
      health: () => get<{ status: string }>('/health'),
      appConfig: () => get<AppConfig>('/app-config'),
      timezones: () => get<string[]>('/timezones'),
      settings: () => get<{ key: string; value: unknown; description: string | null; version: number }[]>('/settings'),
      updateSetting: (key: string, value: unknown, version: number) => put(`/settings/${key}`, { value, version }),
      exchangeRate: () => get<ExchangeRate>('/exchange-rate'),
      refreshExchangeRate: () => request<ExchangeRate>('POST', '/exchange-rate/refresh', {}),
    },

    auth: {
      login: (input: Partial<LoginInput> & { email: string; password: string }) =>
        request<LoginResponse>('POST', '/auth/login', { platform: opts.mode === 'cookie' ? 'web' : undefined, ...input }),
      logout: () => request<void>('POST', '/auth/logout', {}),
      me: () => get<Me>('/me'),
      updateProfile: (input: UpdateMyProfileInput) => request<Me>('PATCH', '/me', input),
      changePassword: (input: ChangePasswordInput) => request<void>('POST', '/auth/change-password', input),
      sessions: () => get<Session[]>('/auth/sessions'),
      revokeSession: (id: string) => del<void>(`/auth/sessions/${id}`),
      revokeOtherSessions: () => request<void>('POST', '/auth/sessions/revoke-others', {}),
    },

    files: {
      presign: (input: PresignUploadInput) => post<PresignedUpload>('/files/presign', input),
      /** Presign, then PUT the file straight to storage. Returns the uploadKey to attach. */
      async upload(file: Blob & { name?: string; type: string; size: number }): Promise<string> {
        const p = await post<PresignedUpload>('/files/presign', {
          fileName: file.name ?? 'upload',
          contentType: file.type,
          size: file.size,
        });
        const res = await doFetch(p.uploadUrl, { method: p.method, headers: p.headers, body: file });
        if (!res.ok) throw new ApiClientError(res.status, 'UPLOAD_FAILED', 'The file could not be uploaded');
        return p.uploadKey;
      },
      /**
       * Upload a large file in parts (each under the proxy's body limit).
       * Returns the upload id to pass to the import that uses it. Each part is
       * retried up to 3 times; onProgress gets the bytes sent so far.
       */
      async uploadInParts(file: Blob, fileName: string, onProgress?: (sent: number, total: number) => void): Promise<string> {
        const up = await post<PartUpload>('/files/parts', { fileName, size: file.size });
        let sent = 0;
        for (let index = 0; sent < file.size; index++) {
          const chunk = file.slice(sent, Math.min(sent + up.partSize, file.size));
          for (let attempt = 1; ; attempt++) {
            try {
              const res = await doFetch(`${root}/files/parts/${up.uploadId}/${index}`, {
                method: 'PUT',
                headers: await headers({ 'Content-Type': 'application/octet-stream' }),
                body: chunk,
                credentials: opts.mode === 'cookie' ? 'include' : 'omit',
              });
              await handle<PartUpload>(res);
              break;
            } catch (err) {
              if (attempt >= 3 || (err instanceof ApiClientError && err.status < 500 && err.status !== 409)) throw err;
            }
          }
          sent += chunk.size;
          onProgress?.(sent, file.size);
        }
        return up.uploadId;
      },
    },

    branches: {
      list: (q?: Partial<ListQuery>) => get<Page<Branch>>('/branches', q),
      get: (id: string) => get<Branch>(`/branches/${id}`),
      create: (input: BranchCreateInput) => post<Branch>('/branches', input),
      update: (id: string, input: BranchUpdateInput) => patch<Branch>(`/branches/${id}`, input),
      setActive: (id: string, input: ActiveToggleInput) => post<Branch>(`/branches/${id}/active`, input),
      export: () => download('/branches/export.xlsx'),
    },

    roles: {
      permissions: () => get<PermissionInfo[]>('/permissions'),
      list: () => get<Role[]>('/roles'),
      create: (input: RoleCreateInput) => post<Role>('/roles', input),
      update: (id: string, input: RoleUpdateInput) => patch<Role>(`/roles/${id}`, input),
      setPermissions: (id: string, input: RolePermissionsInput) => put<Role>(`/roles/${id}/permissions`, input),
      remove: (id: string) => del<void>(`/roles/${id}`),
    },

    users: {
      list: (q?: Partial<ListQuery> & { roleId?: string; branchId?: string }) => get<Page<User>>('/users', q),
      get: (id: string) => get<User>(`/users/${id}`),
      create: (input: UserCreateInput) => post<User>('/users', input),
      update: (id: string, input: UserUpdateInput) => patch<User>(`/users/${id}`, input),
      setActive: (id: string, input: ActiveToggleInput) => post<User>(`/users/${id}/active`, input),
      resetPassword: (id: string, input: ResetPasswordInput) => post<void>(`/users/${id}/reset-password`, input),
      sessions: (id: string) => get<Session[]>(`/users/${id}/sessions`),
      revokeSession: (id: string, sessionId: string) => del<void>(`/users/${id}/sessions/${sessionId}`),
      revokeAllSessions: (id: string) => del<void>(`/users/${id}/sessions`),
      export: () => download('/users/export.xlsx'),
    },

    suppliers: {
      list: (q?: Partial<ListQuery>) => get<Page<Supplier>>('/suppliers', q),
      create: (input: SupplierCreateInput) => post<Supplier>('/suppliers', input),
      update: (id: string, input: SupplierUpdateInput) => patch<Supplier>(`/suppliers/${id}`, input),
      setActive: (id: string, input: ActiveToggleInput) => post<Supplier>(`/suppliers/${id}/active`, input),
    },

    categories: {
      list: (includeInactive = false) => get<Category[]>('/categories', { includeInactive }),
      create: (input: CategoryCreateInput) => post<Category>('/categories', input),
      update: (id: string, input: CategoryUpdateInput) => patch<Category>(`/categories/${id}`, input),
      setActive: (id: string, input: ActiveToggleInput) => post<Category>(`/categories/${id}/active`, input),
    },

    items: {
      list: (q?: Partial<ItemListQuery>) => get<Page<Item>>('/items', q),
      get: (id: string) => get<Item>(`/items/${id}`),
      create: (input: ItemCreateInput) => post<Item>('/items', input),
      update: (id: string, input: ItemUpdateInput) => patch<Item>(`/items/${id}`, input),
      setActive: (id: string, input: ActiveToggleInput) => post<Item>(`/items/${id}/active`, input),
      attachImage: (id: string, uploadKey: string) => post<Item>(`/items/${id}/images`, { uploadKey }),
      /** How one supplier packs the item; all fields empty removes that supplier's packing */
      setPacking: (id: string, input: ItemPackingInput) => put<Item>(`/items/${id}/packing`, input),
      removeImage: (id: string, imageId: string) => del<Item>(`/items/${id}/images/${imageId}`),
      import: (file: Blob, fileName: string, mode: 'validate' | 'commit') => upload<ImportReport>(`/items/import?mode=${mode}`, file, fileName),
      export: (q?: Partial<ItemListQuery>) => download('/items/export.xlsx', q),
    },

    stock: {
      list: (q?: Partial<StockQuery>) => get<StockPage>('/stock', q),
      export: (q?: Partial<StockQuery>) => download('/stock/export.xlsx', q),
      openingImport: (file: Blob, fileName: string, mode: 'validate' | 'commit') =>
        upload<ImportReport>(`/stock/opening-import?mode=${mode}`, file, fileName),
      /** Sample .xlsx in the import layout (sku, branch_code, quantity) with the location codes */
      openingTemplate: () => download('/stock/opening-import/template.xlsx'),
      /** Opening stock imports, newest first; `canRevert` marks the one that can be undone */
      openingImports: () => get<StockImportSummary[]>('/stock/opening-imports'),
      openingImportDetail: (id: string) => get<StockImportDetail>(`/stock/opening-imports/${id}`),
      /** Undo the most recent import; 409 STALE_VERSION or STOCK_CHANGED_SINCE_IMPORT if stock moved since */
      revertOpeningImport: (id: string, input: StockImportRevertInput) => post<StockImportSummary>(`/stock/opening-imports/${id}/revert`, input),
      movements: (itemId: string) =>
        get<{ id: string; type: string; quantity: number; reference: string | null; note: string | null; branchName: string; userName: string | null; createdAt: string }[]>(
          `/stock/items/${itemId}/movements`,
        ),
      /** Needs stock.display.manage; `version` comes from the stock by location report */
      setDisplay: (input: DisplayStockInput) => put<StockByLocationCell>('/stock/display', input),
    },

    /** Home screen summary; sections the user may not see are left out */
    dashboard: () => get<Dashboard>('/dashboard'),

    reports: {
      stockByLocation: (q?: Partial<StockByLocationQuery>) => get<StockByLocationPage>('/reports/stock-by-location', q),
      stockByLocationExport: (q?: Partial<StockByLocationQuery>) => download('/reports/stock-by-location/export.xlsx', q),
      /** Needs items.cost.view as well as reports.view */
      valuation: (q?: Partial<ValuationQuery>) => get<ValuationReport>('/reports/valuation', q),
      valuationExport: (q?: Partial<ValuationQuery>) => download('/reports/valuation/export.xlsx', q),
      lowStock: (q?: Partial<LowStockQuery>) => get<LowStockPage>('/reports/low-stock', q),
      lowStockExport: (q?: Partial<LowStockQuery>) => download('/reports/low-stock/export.xlsx', q),
      slowStock: (q?: Partial<SlowStockQuery>) => get<SlowStockPage>('/reports/slow-stock', q),
      slowStockExport: (q?: Partial<SlowStockQuery>) => download('/reports/slow-stock/export.xlsx', q),
      movements: (q?: Partial<MovementReportQuery>) => get<MovementPage>('/reports/movements', q),
      movementsExport: (q?: Partial<MovementReportQuery>) => download('/reports/movements/export.xlsx', q),
      /** Stock in and out per day / week / month for the chart, same filters as movements (no paging) */
      movementTotals: (q?: Partial<MovementTotalsQuery>) => get<MovementTotals>('/reports/movements/totals', q),
    },

    audit: {
      list: (q?: Partial<AuditQuery>) => get<Page<AuditEntry>>('/audit', q),
      tables: () => get<string[]>('/audit/tables'),
    },

    chinaOrders: {
      share: (id: string, page = 1) => sharePicture(`/orders/${id}/share.png`)(page),
      list: (q?: Partial<ChinaOrderListQuery>) => get<ChinaOrderPage>('/orders', q),
      get: (id: string) => get<ChinaOrder>(`/orders/${id}`),
      cycle: () => get<CycleInfo>('/orders/cycle'),
      inFlight: (itemIds: string[]) => (itemIds.length ? get<InFlightItem[]>('/orders/in-flight', { itemIds: itemIds.join(',') }) : Promise.resolve([] as InFlightItem[])),
      /** Order tracker: every line of submitted orders, oldest first */
      lines: (q?: Partial<OrderLineListQuery>) => get<Page<OrderLineRow>>('/orders/lines', q),
      exportLines: (q?: Partial<OrderLineListQuery>) => download('/orders/lines/export.xlsx', q),
      /** Read (mode preview) or import (mode commit) the warehouse order sheet uploaded with files.uploadInParts; poll importJob */
      importSheet: (input: SheetImportStartInput) => post<ImportJob>('/orders/import-sheet', input),
      importJob: (jobId: string) => get<ImportJob>(`/orders/import-jobs/${jobId}`),
      create: (input: ChinaOrderCreateInput) => post<ChinaOrder>('/orders', input),
      update: (id: string, input: ChinaOrderUpdateInput) => patch<ChinaOrder>(`/orders/${id}`, input),
      addLine: (id: string, input: ChinaOrderLineCreateInput) => post<ChinaOrder>(`/orders/${id}/lines`, input),
      updateLine: (id: string, lineId: string, input: ChinaOrderLineUpdateInput) => patch<ChinaOrder>(`/orders/${id}/lines/${lineId}`, input),
      removeLine: (id: string, lineId: string) => del<ChinaOrder>(`/orders/${id}/lines/${lineId}`),
      submit: (id: string, input: VersionOnlyInput) => post<ChinaOrder>(`/orders/${id}/submit`, input),
      cancel: (id: string, input: StatusReasonInput) => post<ChinaOrder>(`/orders/${id}/cancel`, input),
      supplierEdit: (id: string, lineId: string, input: SupplierLineEditInput) => post<ChinaOrder>(`/orders/${id}/lines/${lineId}/supplier-edit`, input),
      confirm: (id: string, input: VersionOnlyInput) => post<ChinaOrder>(`/orders/${id}/confirm`, input),
      reject: (id: string, input: StatusReasonInput) => post<ChinaOrder>(`/orders/${id}/reject`, input),
      /** Receive in full every open shipment carrying a shipped or partly shipped order (optionally stocking goods that arrived before I-GOAL), then close it as received */
      receive: (id: string, input: ChinaOrderReceiveInput) => post<ChinaOrder>(`/orders/${id}/receive`, input),
      progress: (id: string, lineId: string, input: LineProgressInput) => post<ChinaOrder>(`/orders/${id}/lines/${lineId}/progress`, input),
      serials: (id: string, lineId: string) => get<string[]>(`/orders/${id}/lines/${lineId}/serials`),
      setSerials: (id: string, lineId: string, input: SerialsInput) => put<ChinaOrderLine & { serials: string[] }>(`/orders/${id}/lines/${lineId}/serials`, input),
      attach: (id: string, input: AttachmentCreateInput) => post<ChinaOrder>(`/orders/${id}/attachments`, input),
      removeAttachment: (id: string, attachmentId: string) => del<ChinaOrder>(`/orders/${id}/attachments/${attachmentId}`),
      export: (id: string) => download(`/orders/${id}/export.xlsx`),
    },

    shipments: {
      share: (id: string, page = 1) => sharePicture(`/shipments/${id}/share.png`)(page),
      list: (q?: Partial<ShipmentListQuery>) => get<Page<ShipmentSummary>>('/shipments', q),
      get: (id: string) => get<Shipment>(`/shipments/${id}`),
      export: (q?: Partial<ShipmentListQuery>) => download('/shipments/export.xlsx', q),
      /**
       * Upload a supplier invoice (.xlsx). preview shows what will happen without
       * saving; commit saves. Pass shipmentId to add it to an existing shipment.
       */
      importInvoice: (file: Blob, fileName: string, mode: 'preview' | 'commit', options: Partial<InvoiceImportOptions> = {}, shipmentId?: string) =>
        upload<InvoicePreview | Shipment>(shipmentId ? `/shipments/${shipmentId}/import?mode=${mode}` : `/shipments/import?mode=${mode}`, file, fileName, {
          options: JSON.stringify(options),
        }),
      /** Ship confirmed order lines without an invoice file; finds or creates the ship no and invoice */
      fromOrders: (input: ShipmentFromOrdersInput) => post<Shipment>('/shipments/from-orders', input),
      create: (input: ShipmentCreateInput) => post<Shipment>('/shipments', input),
      update: (id: string, input: ShipmentUpdateInput) => patch<Shipment>(`/shipments/${id}`, input),
      arrive: (id: string, input: ShipmentArriveInput) => post<Shipment>(`/shipments/${id}/arrive`, input),
      receive: (id: string, input: ShipmentReceiveInput) => post<Shipment>(`/shipments/${id}/receive`, input),
      cancel: (id: string, input: StatusReasonInput) => post<Shipment>(`/shipments/${id}/cancel`, input),
      /** Delete a shipment still in transit, with its invoices and lines; its order lines go back to left to ship */
      remove: (id: string, version?: number) => del<void>(`/shipments/${id}${version === undefined ? '' : `?version=${version}`}`),
      /** Number the cartons per supplier (mark-001 …) in line order */
      numberCartons: (id: string, input: ShipmentNumberCartonsInput) => post<Shipment>(`/shipments/${id}/number-cartons`, input),
      /** Carton labels PDF, one label per numbered carton */
      labels: (id: string, supplierId?: string) => download(`/shipments/${id}/labels.pdf`, supplierId ? { supplierId } : undefined),
      addInvoice: (id: string, input: ShipmentInvoiceCreateInput) => post<Shipment>(`/shipments/${id}/invoices`, input),
      updateInvoice: (id: string, invoiceId: string, input: ShipmentInvoiceUpdateInput) => patch<Shipment>(`/shipments/${id}/invoices/${invoiceId}`, input),
      removeInvoice: (id: string, invoiceId: string) => del<Shipment>(`/shipments/${id}/invoices/${invoiceId}`),
      addLine: (id: string, input: ShipmentLineCreateInput) => post<Shipment>(`/shipments/${id}/lines`, input),
      /** @deprecated Goods on a ship are locked (31 Oct 2026); the API refuses with SHIPMENT_LINES_LOCKED */
      updateLine: (id: string, lineId: string, input: ShipmentLineUpdateInput) => patch<Shipment>(`/shipments/${id}/lines/${lineId}`, input),
      /** @deprecated Goods on a ship are locked (31 Oct 2026); delete the whole shipment while in transit instead */
      removeLine: (id: string, lineId: string) => del<Shipment>(`/shipments/${id}/lines/${lineId}`),
    },

    /** The invoice register across all ships (Phase 4) */
    invoices: {
      list: (q?: Partial<InvoiceListQuery>) => get<Page<InvoiceRegisterRow>>('/invoices', q),
      get: (id: string) => get<InvoiceRegisterRow>(`/invoices/${id}`),
      export: (q?: Partial<InvoiceListQuery>) => download('/invoices/export.xlsx', q),
      check: (id: string, input: InvoiceCheckInput) => post<InvoiceRegisterRow>(`/invoices/${id}/check`, input),
      /** Send a received invoice's goods on from where it was received to a branch */
      transfer: (id: string, input: InvoiceTransferInput) => post<Transfer>(`/invoices/${id}/transfer`, input),
    },

    transfers: {
      list: (q?: Partial<TransferListQuery>) => get<Page<TransferSummary>>('/transfers', q),
      get: (id: string) => get<Transfer>(`/transfers/${id}`),
      export: (q?: Partial<TransferListQuery>) => download('/transfers/export.xlsx', q),
      deliveryNote: (id: string) => download(`/transfers/${id}/delivery-note.pdf`),
      create: (input: TransferCreateInput) => post<Transfer>('/transfers', input),
      receive: (id: string, input: TransferReceiveInput) => post<Transfer>(`/transfers/${id}/receive`, input),
      cancel: (id: string, input: StatusReasonInput) => post<Transfer>(`/transfers/${id}/cancel`, input),
    },

    notifications: {
      list: (q?: Partial<NotificationListQuery>) => get<Page<Notification> & { unreadCount: number }>('/notifications', q),
      markRead: (ids: string[]) => request<void>('POST', '/notifications/read', { ids }),
      markAllRead: () => request<void>('POST', '/notifications/read', { all: true }),
      /** Server-sent events URL: a `notification` event whenever something new arrives (web: EventSource with cookies) */
      streamUrl: () => `${root}/notifications/stream`,
      settings: () => get<NotificationSettings>('/notifications/settings'),
      saveSetting: (input: NotificationSettingInput) => put<NotificationSettings>('/notifications/settings', input),
    },

    pipelineReports: {
      onTheWay: (q?: Partial<OnTheWayQuery>) => get<OnTheWayReport>('/reports/on-the-way', q),
      onTheWayExport: (q?: Partial<OnTheWayQuery>) => download('/reports/on-the-way/export.xlsx', q),
      supplierPerformance: (q?: Partial<SupplierPerformanceQuery>) => get<SupplierPerformanceReport>('/reports/supplier-performance', q),
      supplierPerformanceExport: (q?: Partial<SupplierPerformanceQuery>) => download('/reports/supplier-performance/export.xlsx', q),
      freight: (q?: Partial<FreightQuery>) => get<FreightReport>('/reports/freight', q),
      freightExport: (q?: Partial<FreightQuery>) => download('/reports/freight/export.xlsx', q),
    },

    adjustments: {
      list: (q?: Partial<AdjustmentListQuery>) => get<Page<AdjustmentSummary>>('/adjustments', q),
      get: (id: string) => get<Adjustment>(`/adjustments/${id}`),
      export: (q?: Partial<AdjustmentListQuery>) => download('/adjustments/export.xlsx', q),
      /** Every active item with what is on hand at the location; fill in the Counted column */
      countSheet: (branchId: string) => download('/adjustments/count-sheet.xlsx', { branchId }),
      countImport: (branchId: string, file: Blob, fileName: string, mode: 'validate' | 'commit', note?: string) =>
        upload<CountPreview>(`/adjustments/count-import${qs({ branchId, mode, note })}`, file, fileName),
      create: (input: AdjustmentCreateBody) => post<Adjustment>('/adjustments', input),
      approve: (id: string, input: AdjustmentDecideInput) => post<Adjustment>(`/adjustments/${id}/approve`, input),
      reject: (id: string, input: AdjustmentDecideInput) => post<Adjustment>(`/adjustments/${id}/reject`, input),
      cancel: (id: string, input: AdjustmentDecideInput) => post<Adjustment>(`/adjustments/${id}/cancel`, input),
    },

    branchOrders: {
      /** Items running low at the shop, with how many to order */
      suggestions: (branchId?: string) => get<BranchOrderSuggestion[]>('/branch-orders/suggestions', { branchId }),
      share: (id: string, page = 1) => sharePicture(`/branch-orders/${id}/share.png`)(page),
      list: (q?: Partial<BranchOrderListQuery>) => get<Page<BranchOrderSummary>>('/branch-orders', q),
      get: (id: string) => get<BranchOrder>(`/branch-orders/${id}`),
      export: (q?: Partial<BranchOrderListQuery>) => download('/branch-orders/export.xlsx', q),
      create: (input: BranchOrderCreateBody) => post<BranchOrder>('/branch-orders', input),
      update: (id: string, input: BranchOrderUpdateBody) => put<BranchOrder>(`/branch-orders/${id}`, input),
      place: (id: string, input: { version: number }) => post<BranchOrder>(`/branch-orders/${id}/place`, input),
      cancel: (id: string, input: StatusReasonInput) => post<BranchOrder>(`/branch-orders/${id}/cancel`, input),
      send: (id: string, input: BranchOrderSendBody) => post<{ order: BranchOrder; transferId: string }>(`/branch-orders/${id}/send`, input),
      decline: (id: string, input: BranchOrderDeclineInput) => post<BranchOrder>(`/branch-orders/${id}/decline`, input),
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
